import { useNewCode, useFlag } from '@calendly/feature-check-react'
import { useBooleanState } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { useDropdown } from '@calendly/ui/components/dropdown'
import { IconButton } from '@calendly/ui/components/icon-button'
import { SettingsMenuVerticalIcon } from '@calendly/ui/components/icons'
import { useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useGetUserQuery } from 'common'
import { HostReschedule } from 'features/host_reschedule'
import { useGetMeetingIntelligenceUserSettingsQuery } from 'features/meeting_intelligence'
import { DeleteRecapsModal } from 'features/tracked_meetings'
import { useFetchTrackedMeetingByForeignIdQuery } from 'hooks/tracked_meetings/api'
import { isCalendlyMeeting } from 'shared/utils/meeting_helpers'

import { CancelMeetingModal } from './cancel_meeting_modal'
import { MoreActionsMenu, RescheduleMenuItem } from './more_actions_menu'
import { useRescheduleVisibility } from './use_reschedule_visibility'
import { isPendingEvent } from '../utils'

export const MoreActions = ({ event,
  inProgress = false,
  showReportModal, clearSelectedEvent, updateSelectedEvent, sendAnalyticsForReschedule, showCancelInRow = false }) => {
  const [toggle, Dropdown, isExpanded] = useDropdown()
  const [isCancelModalOpen, openCancelModal, closeCancelModal] = useBooleanState(false)
  const [isDeleteRecapModalOpen, openDeleteRecapModal, closeDeleteRecapModal] = useBooleanState(false)
  const rescheduleSlotRef = useRef(null)
  const [rescheduleSlotReady, setRescheduleSlotReady] = useState(false)
  const { data: currentUser } = useGetUserQuery()
  const isNotPretending = currentUser && !currentUser.is_pretending

  const isMeetingIntelligenceEnabled = useNewCode('meeting_intelligence')?.enabled
  const { data: meetingIntelligence } = useFetchTrackedMeetingByForeignIdQuery(
    event?.uuid,
    { skip: !event || isPendingEvent(event) || !isMeetingIntelligenceEnabled }
  )
  const { data: meetingIntelligenceSettings } = useGetMeetingIntelligenceUserSettingsQuery({})

  const shouldShowRemoveNotetakerItem = isNotPretending
    && !!meetingIntelligenceSettings?.miEnabled
    && meetingIntelligence?.enabled
    && ['Scheduled'].includes(meetingIntelligence?.status)
  /* Show Cancel in menu when it's not in the row (overflow or phone+notetaker). */
  const shouldShowCancelItem = event.policy?.cancel && isNotPretending && !showCancelInRow
  const shouldShowDeleteItem = Boolean(meetingIntelligence?.trackedMeetingUuid)
    && meetingIntelligence?.status === 'Completed'
    && !meetingIntelligence?.deleted
  const featureFlagEnabled = useFlag('trust_and_safety_reactive_block')
  const shouldShowReportItem = !event?.past && isCalendlyMeeting(event) && !featureFlagEnabled
  const { showRescheduleInDropdown: shouldShowRescheduleItem } = useRescheduleVisibility(event, inProgress)

  const shouldShowMenu = [
    shouldShowRemoveNotetakerItem,
    shouldShowCancelItem,
    shouldShowDeleteItem,
    shouldShowReportItem,
    shouldShowRescheduleItem,
  ].some(Boolean)

  const PortaledRescheduleButton = useCallback(
    ({ onClick }) => {
      if (!rescheduleSlotReady || !rescheduleSlotRef.current) return null
      return createPortal(<RescheduleMenuItem onClick={onClick} />, rescheduleSlotRef.current)
    },
    [rescheduleSlotReady]
  )

  if (!shouldShowMenu) return null

  return (
    <>
      <IconButton aria-label={I18n.t('publisher.meetings.actions.more_actions')} onClick={toggle} aria-expanded={isExpanded}>
        <SettingsMenuVerticalIcon />
      </IconButton>
      <Dropdown>
        <MoreActionsMenu
          event={event}
          isExpanded={isExpanded}
          meetingIntelligence={meetingIntelligence}
          openCancelModal={openCancelModal}
          openDeleteRecapModal={openDeleteRecapModal}
          shouldShowCancelItem={shouldShowCancelItem}
          shouldShowDeleteItem={shouldShowDeleteItem}
          shouldShowMenu={shouldShowMenu}
          shouldShowRemoveNotetakerItem={shouldShowRemoveNotetakerItem}
          shouldShowReportItem={shouldShowReportItem}
          shouldShowRescheduleItem={shouldShowRescheduleItem}
          rescheduleSlotRef={rescheduleSlotRef}
          onRescheduleSlotMount={setRescheduleSlotReady}
          sendAnalyticsForReschedule={sendAnalyticsForReschedule}
          showReportModal={showReportModal}
          toggleDropdown={toggle}
        />
      </Dropdown>
      {shouldShowRescheduleItem && (
        <HostReschedule
          event={event}
          invitee={event.invitee}
          Button={PortaledRescheduleButton}
          sendAnalytics={sendAnalyticsForReschedule}
        />
      )}
      {isCancelModalOpen && (
        <CancelMeetingModal
          event={event}
          onClose={closeCancelModal}
          clearSelectedEvent={clearSelectedEvent}
          updateSelectedEvent={updateSelectedEvent}
          showReportModal={showReportModal}
        />
      )}
      {shouldShowDeleteItem && (
        <DeleteRecapsModal
          isOpen={isDeleteRecapModalOpen}
          meetingUuids={[meetingIntelligence.trackedMeetingUuid]}
          onClose={closeDeleteRecapModal}
          onSuccess={closeDeleteRecapModal}
        />
      )}
    </>
  )
}
